import {
    DatePicker,
    LocalizationProvider,
    PickersDay,
    PickersDayProps,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import styles from '../Planning.module.scss';
import React, { useEffect } from 'react';
import locale from 'date-fns/locale/en-US';
import {
    addDays,
    endOfWeek,
    isAfter,
    isBefore,
    isSameDay,
    startOfWeek,
} from 'date-fns';
import { Button, IconButton, Switch, TextField, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Add, ChevronLeft, ChevronRight, Print } from '@mui/icons-material';
import { CollapsibleSearch } from 'components/common/CollapsibleSearch/CollapsibleSearch';
import { FilterBar } from 'components/common/FilterBar/FilterBar';
import { FilterChips } from 'components/common/FilterBar/FilterChips/FilterChips';
import { useAuth, useUserSession } from 'hooks';
import { UserTicketSession } from 'types/User/firebaseTypes';
import { updateUserSession } from 'lib/firebase';
import { convertDeliveryStatusToInternalName } from 'lib/constants';

export interface PlanningToolbarProps {
    currentView: string;
    minDate: Date;
    maxDate: Date;
    setMinDate: (d: Date) => void;
    setMaxDate: (d: Date) => void;
    shouldOnlyShowMyTickets: boolean;
    setShouldOnlyShowMyTickets: (b: boolean) => void;
    statusFilters: Record<string, boolean>;
    setStatusFilters: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >;
    setIsCreateModalOpen: (b: boolean) => void;
    changeWeek: (w: number) => void;
    setSearchTerm: (s: string) => void;
    setIsPrintModalOpen: (b: boolean) => void;
}

export const PlanningToolbar = ({
    currentView,
    minDate,
    maxDate,
    setMinDate,
    setMaxDate,
    shouldOnlyShowMyTickets,
    setShouldOnlyShowMyTickets,
    statusFilters,
    setStatusFilters,
    setIsCreateModalOpen,
    changeWeek,
    setSearchTerm,
    setIsPrintModalOpen,
}: PlanningToolbarProps) => {
    // set first day of calendar modal to monday
    if (locale && locale.options) {
        locale.options.weekStartsOn = 1;
    }

    const session = useUserSession();
    const { user } = useAuth();

    useEffect(() => {
        const userFilters = session?.ticket?.delivery?.filters;
        if (userFilters) {
            setShouldOnlyShowMyTickets(!!userFilters.assignedToMe);
            if (userFilters.status) {
                const userStatus = Object.keys(userFilters.status).reduce(
                    (us, status) => {
                        return userFilters.status
                            ? {
                                  ...us,
                                  [status === 'assigned'
                                      ? 'assigned'
                                      : convertDeliveryStatusToInternalName[
                                            status
                                        ]]:
                                      userFilters.status[
                                          status as keyof object
                                      ],
                              }
                            : { ...us };
                    },
                    {}
                );
                setStatusFilters((prevFilters) => ({
                    ...prevFilters,
                    ...userStatus,
                }));
            }
        }
    }, [session, setShouldOnlyShowMyTickets, setStatusFilters]);

    const updateStatusFilters = (filters: Record<string, boolean>) => {
        if (session?.ticket) {
            const newDeliverySession: UserTicketSession = { ...session.ticket };

            const newFilters = {
                backlog: filters.backlog ?? true,
                done: filters.done ?? false,
                in_progress: filters.in_progress ?? true,
                planned: filters.planned ?? true,
                archived: filters.archived ?? false,
                assigned: filters.assigned ?? false,
            };

            if (newDeliverySession.delivery?.filters)
                newDeliverySession.delivery.filters.status = newFilters;

            if (user?.uid) {
                updateUserSession(user.uid, 'ticket', newDeliverySession);
            }
        }

        return setStatusFilters(filters);
    };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={locale}
        >
            <div className={styles.toolbar} data-testid="PlanningToolbar">
                <div className={styles.filterRow}>
                    <div className={styles.dateRange}>
                        <DatePicker
                            InputProps={{
                                sx: {
                                    '>input': {
                                        padding: '8px',
                                    },
                                },
                            }}
                            label="Start Date"
                            value={
                                currentView === 'week'
                                    ? addDays(startOfWeek(minDate), 1)
                                    : minDate
                            }
                            onChange={(value) => {
                                if (value) {
                                    setMinDate(
                                        currentView === 'week'
                                            ? addDays(startOfWeek(value), 1)
                                            : value
                                    );
                                }
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            showDaysOutsideCurrentMonth={currentView === 'week'}
                            renderDay={(
                                date: Date,
                                selectedDates: Array<Date | null>,
                                pickersDayProps: PickersDayProps<Date>
                            ) => {
                                if (currentView !== 'week') {
                                    return <PickersDay {...pickersDayProps} />;
                                }

                                const start = addDays(startOfWeek(minDate), 1);
                                const end = addDays(endOfWeek(minDate), 1);

                                const isFirstDay = isSameDay(date, start);
                                const isLastDay = isSameDay(date, end);
                                const dayIsBetween =
                                    isFirstDay ||
                                    isLastDay ||
                                    (isBefore(date, end) &&
                                        isAfter(date, start));

                                return (
                                    <CustomPickersDay
                                        {...pickersDayProps}
                                        disableMargin
                                        isFirstDay={isFirstDay}
                                        isLastDay={isLastDay}
                                        dayIsBetween={dayIsBetween}
                                    />
                                );
                            }}
                        />
                        {currentView === 'list' && (
                            <DatePicker
                                label="End Date"
                                value={maxDate}
                                onChange={(value) => {
                                    if (value) {
                                        setMaxDate(value);
                                    }
                                }}
                                InputProps={{
                                    sx: {
                                        '>input': {
                                            padding: '8px',
                                        },
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                        )}
                        {currentView !== 'list' && (
                            <div className={styles.nextPrevButtons}>
                                <Button
                                    title="Previous Week"
                                    onClick={() => changeWeek(-1)}
                                >
                                    <ChevronLeft />
                                </Button>
                                <Button
                                    title="Next Week"
                                    onClick={() => changeWeek(1)}
                                >
                                    <ChevronRight />
                                </Button>
                            </div>
                        )}

                        <FilterBar
                            hideFilterChips={true}
                            filterGroups={[
                                {
                                    name: 'Status',
                                    filters: statusFilters,
                                    onUpdateFilters: updateStatusFilters,
                                },
                            ]}
                        />
                    </div>

                    <FilterChips
                        filterGroups={[
                            {
                                name: 'Status',
                                filters: statusFilters,
                                onUpdateFilters: updateStatusFilters,
                            },
                        ]}
                    />
                </div>
                <div className={styles.toolbarRight}>
                    <CollapsibleSearch
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClose={() => setSearchTerm('')}
                    />
                    <div className={styles.myDeliveries}>
                        <Tooltip title="Show my deliveries only">
                            <Switch
                                name="my-deliveries"
                                checked={shouldOnlyShowMyTickets}
                                onChange={(e) => {
                                    if (
                                        session?.ticket?.delivery?.filters &&
                                        user?.uid
                                    ) {
                                        const newDeliverySession: UserTicketSession =
                                            {
                                                ...session.ticket,
                                                delivery: {
                                                    ...session.ticket.delivery,
                                                    filters: {
                                                        ...session.ticket
                                                            .delivery.filters,
                                                        assignedToMe:
                                                            e.target.checked,
                                                    },
                                                },
                                            };

                                        // newDeliverySession.delivery.filters.assignedToMe =
                                        //     e.target.checked;

                                        updateUserSession(
                                            user.uid,
                                            'ticket',
                                            newDeliverySession
                                        );
                                    }

                                    setShouldOnlyShowMyTickets(
                                        e.target.checked
                                    );
                                }}
                            />
                        </Tooltip>
                        <label htmlFor="my-deliveries">My Deliveries</label>
                    </div>
                    <IconButton
                        title="Print selected tickets"
                        onClick={() => setIsPrintModalOpen(true)}
                    >
                        <Print />
                    </IconButton>
                    <Button
                        className={styles.createDeliveryButton}
                        onClick={() => setIsCreateModalOpen(true)}
                        startIcon={<Add />}
                    >
                        New Delivery
                    </Button>
                </div>
            </div>
        </LocalizationProvider>
    );
};

// for week view custom highlighting
interface CustomPickerDayProps extends PickersDayProps<Date> {
    dayIsBetween: boolean;
    isFirstDay: boolean;
    isLastDay: boolean;
}

const CustomPickersDay = styled(PickersDay, {
    shouldForwardProp: (prop) =>
        prop !== 'dayIsBetween' &&
        prop !== 'isFirstDay' &&
        prop !== 'isLastDay',
})<CustomPickerDayProps>(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
    ...(dayIsBetween && {
        borderRadius: 0,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        '&:hover, &:focus': {
            backgroundColor: theme.palette.primary.dark,
        },
    }),
    ...(isFirstDay && {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    }),
    ...(isLastDay && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    }),
})) as React.ComponentType<CustomPickerDayProps>;
